import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Box,
  Stack,
  List,
  ListItem,
  ListItemButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faThumbTack,
  faThumbTackSlash,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  editChatTitle,
  getChatHistory,
  deleteChat,
  getChatTitles,
  togglePinChat,
} from "../../actions/chatActions";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import { deductCredits } from "../../utils/HelperFunction/deductCredits";
import { showInsufficientCreditsModal } from "../../actions/creditActions";

const Sidebar = ({ closeSidebar }) => {
  const dispatch = useDispatch();
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const [selectedChat, setSelectedChat] = useState({ category: "", index: -1 });
  const [hoveredChat, setHoveredChat] = useState({ category: "", index: -1 });
  const [newTitle, setNewTitle] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedChatTitle, setSelectedChatTitle] = useState(null);
  const [isEditing, setIsEditing] = useState({ category: "", index: -1 });
  const credits = useSelector((state) => state.credit.credits); // Get credits from Redux store
  const userId = useSelector((state) => state.credit.userId); // Get userId from Redux store
  const chatTitles = useSelector((state) => state.chatTitles);
  const { loading, titles, error } = chatTitles;
  const chatHistory = useSelector((state) => state.chatHistory);
  const { history } = chatHistory;

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const inputRef = useRef(null);

  const pinnedChats = titles?.filter((chat) => chat.is_pinned);
  const recentChats = titles?.filter((chat) => !chat.is_pinned);

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleTogglePin = (event, category, chatId) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    event.stopPropagation();
    const isPinned = category === "recent" ? false : true;
    dispatch(togglePinChat(userInfo._id, chatId, isPinned));
    setSelectedChat({ category: "", index: -1 });
    const deductionSuccess = deductCredits(
      {
        userId,
        creditsToDeduct: 1,
        event: "Pin/Unpin chat on Legal Chat Page",
      },
      dispatch
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification."
      );
    }
  };

  const handleListItemClick = (category, index, chatId) => {
    isMobileView && closeSidebar();
    const isSameSelection =
      selectedChat.index === index && selectedChat.category === category;
    const hasLoadedHistoryForChat =
      history?.chat_id === chatId && Array.isArray(history?.messages) && history.messages.length > 0;

    if (isSameSelection && hasLoadedHistoryForChat)
      return;
    setSelectedChat({ category, index });
    dispatch(getChatHistory(userInfo._id, chatId));
  };

  const handleNewChatClick = () => {
    isMobileView && closeSidebar();
    setSelectedChat(-1);
    dispatch({ type: "CHAT_HISTORY_RESET" });
    dispatch({ type: "NEW_CHAT_SUCCESS" });
  };

  const handleEditTitle = (event, category, index, title) => {
    event.stopPropagation();
    setIsEditing({ category, index });
    setNewTitle(title);
  };

  const handleSaveEdit = async (chatId) => {
    if (newTitle.trim()) {
      let creditsToDeduct = 1;
      if (credits <= 0 || credits <= creditsToDeduct) {
        dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
        return;
      }
      try {
        await dispatch(editChatTitle(userInfo._id, chatId, newTitle));
        await dispatch(getChatTitles(userInfo._id));
        setNewTitle("");
        setIsEditing({ category: "", index: -1 });
        const deductionSuccess = deductCredits(
          { userId, creditsToDeduct: 1, event: "Edit title on Legal Chat Page" },
          dispatch
        );

        if (!deductionSuccess) {
          console.warn(
            "Failed to deduct credits. Ensure retry or user notification."
          );
        }
      } catch (error) {
        toast.error(error.message || "Failed to update chat title", {
          position: "top-right",
        });
      }
    }
  };

  const handleCancelEdit = (index) => {
    setNewTitle("");
    setIsEditing({ category: "", index: -1 });
  };

  const handleConfirmDelete = (chatId) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    dispatch(deleteChat(userInfo._id, chatId));
    dispatch({ type: "CHAT_HISTORY_RESET" });

    // Handle resetting the selected chat if it's the one being deleted
    setSelectedChat({ category: "", index: -1 });

    // Close the delete dialog
    setOpenDeleteDialog(false);
    const deductionSuccess = deductCredits(
      { userId, creditsToDeduct: 1, event: "Delete Chat in Legal Chat Page" },
      dispatch
    );

    if (!deductionSuccess) {
      console.warn(
        "Failed to deduct credits. Ensure retry or user notification."
      );
    }
  };

  const handleDeleteChat = (event, category, index) => {
    event.preventDefault();
    event.stopPropagation();

    // Determine the correct chat list (pinned or recent)
    const findChat =
      category === "pinned" ? pinnedChats[index] : recentChats[index];

    setSelectedChatTitle({
      title: findChat.chat_title,
      index,
      isPinned: findChat.isPinned,
      chatId: findChat.chat_id,
    });

    // Open delete dialog
    setOpenDeleteDialog(true);
  };

  const handleInputChange = (e) => {
    const cursorPosition = inputRef.current.selectionStart;
    setNewTitle(e.target.value);

    // Set the cursor back to where it was
    setTimeout(() => {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  useEffect(() => {
    if (error) {
      toast.error(error, { position: "top-right" });
    }
  }, [error]);

  useEffect(() => {
    dispatch(getChatTitles(userInfo?._id));
  }, [userInfo, dispatch]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        width: { xs: "278px", lg: "16.6%" },
        // minWidth: '278px',
        height: { xs: "100%", md: "calc(100vh - 50px)" },
        p: "12px 0px 0 12px",
        bgcolor: "#f5f5f5",
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          boxSizing: "border-box",
          flexDirection: "column",
          bgcolor: "#fff",
          p: "8px",
          borderRadius: "8px",
          flexGrow: 1,
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: "#003D78",
            display: "flex",
            justifyContent: "center",
            marginBottom: "8px",
            mr: 0.5,
            marginTop: "8px",
            textTransform: "none",
            "&:hover": { backgroundColor: "primary.main", color: "#fff" },
            transition: "background-color 0.3s ease-in-out",
          }}
          onClick={handleNewChatClick}
        >
          New Chat
        </Button>
        {loading ? (
          <Box
            sx={{
              margin: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : titles?.length > 0 ? (
          <List
            sx={{
              overflowY: "auto",
              "::-webkit-scrollbar": {
                width: "5px",
              },
              "::-webkit-scrollbar-track": {
                background: "#fff",
              },
              "::-webkit-scrollbar-thumb": {
                background: "#bbb",
                borderRadius: "6px",
              },
            }}
          >
            {pinnedChats?.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  sx={{
                    display: "flex",
                    boxSizing: "border-box",
                    gap: 0.5,
                    fontSize: "14px",
                    alignItems: "center",
                    padding: "4px 16px 0px 12px",
                    color: "#3C50B1",
                    fontWeight: "bold",
                    textTransform: "none",
                  }}
                >
                  Pinned
                  <FontAwesomeIcon icon={faThumbTack} fontSize="12px" />
                </Typography>
                {pinnedChats?.map((title, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      disableRipple
                      sx={{
                        mr: 1,
                        height: "38px",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        "&.Mui-selected": {
                          backgroundColor: "#e8eefc",
                          "&:hover": { backgroundColor: "#e8eefc" },
                        },
                        "&:hover": { backgroundColor: "#e8eefc" },
                      }}
                      selected={
                        selectedChat.category === "pinned" &&
                        selectedChat.index === index
                      }
                      onClick={() =>
                        handleListItemClick("pinned", index, title.chat_id)
                      }
                      onMouseEnter={() =>
                        setHoveredChat({ category: "pinned", index })
                      }
                      onMouseLeave={() =>
                        setHoveredChat({ category: "", index: -1 })
                      }
                    >
                      {isEditing.category === "pinned" &&
                      isEditing.index === index ? (
                        <Stack direction="row" width="100%">
                          <input
                            style={{ width: "100%", marginLeft: "0" }}
                            type="text"
                            autoFocus
                            ref={inputRef}
                            value={newTitle}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            onChange={handleInputChange}
                          />
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveEdit(title.chat_id);
                            }}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEdit();
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          width="100%"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              fontSize: "13px",
                              flexGrow: 1,
                            }}
                          >
                            {title.chat_title}
                          </Typography>
                          {(hoveredChat.category === "pinned" &&
                            hoveredChat.index === index) ||
                          (selectedChat.category === "pinned" &&
                            selectedChat.index === index) ? (
                            <Stack direction="row" alignItems="center" flexShrink={0}>
                              <Tooltip title="Unpin">
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleTogglePin(e, "pinned", title.chat_id)
                                  }
                                  sx={{ p: "2px" }}
                                >
                                  <FontAwesomeIcon
                                    icon={faThumbTackSlash}
                                    fontSize="12px"
                                    style={{ color: "#666" }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit title">
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleEditTitle(
                                      e,
                                      "pinned",
                                      index,
                                      title.chat_title
                                    )
                                  }
                                  sx={{ p: "2px" }}
                                >
                                  <EditIcon sx={{ fontSize: 14, color: "#666" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete chat">
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleDeleteChat(e, "pinned", index)
                                  }
                                  sx={{ p: "2px" }}
                                >
                                  <DeleteIcon
                                    sx={{ fontSize: 14, color: "#d32f2f" }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : null}
                        </Stack>
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </>
            )}
            {recentChats?.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    fontSize: "14px",
                    alignItems: "center",
                    padding: "4px 16px 0px 12px",
                    color: "#3C50B1",
                    fontWeight: "bold",
                    textTransform: "none",
                  }}
                >
                  Recent
                </Typography>
                {recentChats?.map((title, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton
                      disableRipple
                      sx={{
                        mr: 1,
                        height: "38px",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        "&.Mui-selected": {
                          backgroundColor: "#e8eefc",
                          "&:hover": { backgroundColor: "#e8eefc" },
                        },
                        "&:hover": { backgroundColor: "#e8eefc" },
                      }}
                      selected={
                        selectedChat.category === "recent" &&
                        selectedChat.index === index
                      }
                      onClick={() =>
                        handleListItemClick("recent", index, title.chat_id)
                      }
                      onMouseEnter={() =>
                        setHoveredChat({ category: "recent", index })
                      }
                      onMouseLeave={() =>
                        setHoveredChat({ category: "", index: -1 })
                      }
                    >
                      {isEditing.category === "recent" &&
                      isEditing.index === index ? (
                        <Stack direction="row" width="100%">
                          <input
                            style={{ width: "100%", marginLeft: "0" }}
                            type="text"
                            autoFocus
                            ref={inputRef}
                            value={newTitle}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            onChange={handleInputChange}
                          />
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveEdit(title.chat_id);
                            }}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEdit();
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          width="100%"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              fontSize: "13px",
                              flexGrow: 1,
                            }}
                          >
                            {title.chat_title}
                          </Typography>
                          {(hoveredChat.category === "recent" &&
                            hoveredChat.index === index) ||
                          (selectedChat.category === "recent" &&
                            selectedChat.index === index) ? (
                            <Stack direction="row" alignItems="center" flexShrink={0}>
                              <Tooltip title="Pin">
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleTogglePin(e, "recent", title.chat_id)
                                  }
                                  sx={{ p: "2px" }}
                                >
                                  <FontAwesomeIcon
                                    icon={faThumbTack}
                                    fontSize="12px"
                                    style={{ color: "#666" }}
                                  />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit title">
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleEditTitle(
                                      e,
                                      "recent",
                                      index,
                                      title.chat_title
                                    )
                                  }
                                  sx={{ p: "2px" }}
                                >
                                  <EditIcon sx={{ fontSize: 14, color: "#666" }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete chat">
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    handleDeleteChat(e, "recent", index)
                                  }
                                  sx={{ p: "2px" }}
                                >
                                  <DeleteIcon
                                    sx={{ fontSize: 14, color: "#d32f2f" }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          ) : null}
                        </Stack>
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </>
            )}
          </List>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              m: "auto",
            }}
          >
            <Typography
              variant="subtitle2"
              color="textSecondary"
              textAlign="center"
            >
              Start a new chat to see your history here 🚀
            </Typography>
          </Box>
        )}
        <ConfirmationDialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          title="Delete Chat"
          description={`This will delete "${selectedChatTitle?.title}". You won't be able to recover it.`}
          onConfirm={() => handleConfirmDelete(selectedChatTitle?.chatId)}
          confirmText="Delete"
          confirmColor="error"
        />
        <ToastContainer />
      </Box>
    </Box>
  );
};

export default Sidebar;
